/* eslint-disable max-lines */
import { ThemeProvider } from "@mui/material";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import type { FunctionComponent, PartialPropsOf, ReactNode } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import type { Required } from "utility-types";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { article as articleData } from "shared/data";
import { articleSponsorType } from "shared/data/article";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { TakeoverAdSlot } from "scmp-app/components/advertisement/takeover-ad-slot";
import { ArticleImageGallery } from "scmp-app/components/article/article-image-gallery";
import { ImageSize } from "scmp-app/components/article/article-leading/article-leading-images/enums";
import { useArticlePianoPaywallContext } from "scmp-app/components/article/article-piano-paywall/contexts";
import type { Props as ArticlePrintHeadlineMessageProps } from "scmp-app/components/article/article-print-headline-message";
import { FloatingButton } from "scmp-app/components/article/article-render/floating-button";
import { ArticleSpeechPlayerPortal } from "scmp-app/components/article/article-speech/article-speech-player-portal";
import { AuthorAvatarContextProvider } from "scmp-app/components/author/author-avatar/contexts";
import type { ContentBody } from "scmp-app/components/content/content-body";
import { useContentReadMoreContext } from "scmp-app/components/content/content-read-more";
import { ContentReadMoreButton } from "scmp-app/components/content/content-read-more/content-read-more-button";
import { useContentSchemaRenderIframe } from "scmp-app/components/schema-render/common/iframe/hooks";
import { type Props as InlineAdSlotProps } from "scmp-app/components/schema-render/common/inline-ad-slot";
import type { AiSeoOverride } from "scmp-app/lib/ai-seo/types";
import { appAtom } from "scmp-app/lib/app/atoms";
import { ExperimentAPINames, ExperimentTitleNames } from "scmp-app/lib/optimizely/consts";
import { useOptimizelyContext } from "scmp-app/lib/optimizely/context";
import { useActiveExperiment } from "scmp-app/lib/optimizely/hooks";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import { usePaywallPosition } from "scmp-app/lib/rosetta/hooks/rosetta-paywall-position";
import type { articleRenderArticle$key } from "scmp-app/queries/__generated__/articleRenderArticle.graphql";

import { ReadMoreElements } from "./consts";
import { useArticleRenderContext } from "./contexts";
import {
  DynamicGenericArticle,
  DynamicInfographicArticle,
  DynamicLiveArticle,
  DynamicMorningStudioArticle,
  DynamicNewsletterArticle,
  DynamicPhotoEssayAndLongReadArticle,
  DynamicPlusGenericArticle,
  DynamicPostMagazineArticle,
  DynamicSeriesArticle,
  DynamicStyleArticle,
  DynamicVideoArticle,
} from "./dynamics";
import { GlobalChecker } from "./global-checker";
import { checkArticleRenderType, checkIsVideoContent, getArticleType } from "./helpers";
import { withArticleRenderContext } from "./hoc";
import { useMostLikelyToSubscribe, usePianoIntegration } from "./hooks";
import { useArticleBaseAdvertisingInfo } from "./hooks/article-base-advertising-info";
import { checkArticleIsPlusInlinePaywall } from "./hooks/piano-integration/helpers";
import type { InlinePaywallOverlayerProps } from "./styles";
import {
  ArticleRenderContainer,
  ContentReadMoreButtonContainer,
  InlinePaywallOverlayer,
  PrimaryContentBody,
  SecondlyContentBody,
  StyledActionWidget,
  StyledAdSlot,
  StyledArticleActionBar,
  StyledArticleAdditionalInfoBox,
  StyledArticleAuthorAvatars,
  StyledArticleAuthorBlock,
  StyledArticleAuthorNames,
  StyledArticleAuthorsDetails,
  StyledArticleAuthorShortBio,
  StyledArticleBeforeYouGoWidget,
  StyledArticleCarouselWidget,
  StyledArticleCommentsTrigger,
  StyledArticleCorrectionsWidget,
  StyledArticleDate,
  StyledArticleFurtherReadingWidget,
  StyledArticleLeading,
  StyledArticleLiveTag,
  StyledArticleMoreOnThisList,
  StyledArticlePaywallContainer,
  StyledArticleReadingTime,
  StyledArticleRelatedTopics,
  StyledArticleScmpPollWidget,
  StyledArticleSections,
  StyledArticleSeries,
  StyledArticleShareWidget,
  StyledArticleSpeechInlineWidget,
  StyledArticleSponsor,
  StyledArticleTopic,
  StyledArticleTopicFollowWidget,
  StyledArticleTopics,
  StyledArticleTopicWidget,
  StyledArticleVocabBox,
  StyledCommentContainer,
  StyledContentHeadline,
  StyledContentHeadlineTag,
  StyledContentSubHeadline,
} from "./styles";
import { theme as defaultTheme } from "./theme";
import { ArticleRenderType } from "./types";
export * from "./types";

export type DefaultComponentsProvider = {
  actionBar: (props?: PartialPropsOf<typeof StyledArticleActionBar>) => ReactNode;
  actionWidget: (props?: PartialPropsOf<typeof StyledActionWidget>) => ReactNode;
  adslot: (props: Required<PartialPropsOf<typeof StyledAdSlot>, "adUnit" | "sizes">) => ReactNode;
  articleAdditionalInfoBox: (
    props?: PartialPropsOf<typeof StyledArticleAdditionalInfoBox>,
  ) => ReactNode;
  authorAvatars: (props?: PartialPropsOf<typeof StyledArticleAuthorAvatars>) => ReactNode;
  authorBlock: (props?: PartialPropsOf<typeof StyledArticleAuthorBlock>) => ReactNode;
  authorDetails: (props?: PartialPropsOf<typeof StyledArticleAuthorsDetails>) => ReactNode;
  authorNames: (props?: PartialPropsOf<typeof StyledArticleAuthorNames>) => ReactNode;
  authorShortBio: (props?: PartialPropsOf<typeof StyledArticleAuthorShortBio>) => ReactNode;
  beforeYouGoWidget: (
    provider: DefaultComponentsProvider,
    props?: PartialPropsOf<typeof StyledArticleBeforeYouGoWidget>,
  ) => ReactNode;
  body: (
    props?: PartialPropsOf<typeof ContentBody> & {
      isDisabledArticleImageGallery?: boolean;
      paywallVariant?: InlinePaywallOverlayerProps["$variant"];
      printHeadlineVariant?: ArticlePrintHeadlineMessageProps["variant"];
    },
  ) => ReactNode;
  bottomCarouselWidget: (
    provider: DefaultComponentsProvider,
    props?: PartialPropsOf<typeof StyledArticleCarouselWidget> & { shouldDisableAds?: boolean },
  ) => ReactNode;
  commentTrigger: (props?: PartialPropsOf<typeof StyledArticleCommentsTrigger>) => ReactNode;
  correction: (props?: PartialPropsOf<typeof StyledArticleCorrectionsWidget>) => ReactNode;
  furtherReading: (props?: PartialPropsOf<typeof StyledArticleFurtherReadingWidget>) => ReactNode;
  headline: (props?: PartialPropsOf<typeof StyledContentHeadline>) => ReactNode;
  headlineTag: (props?: PartialPropsOf<typeof StyledContentHeadlineTag>) => ReactNode;
  leading: (
    props?: PartialPropsOf<typeof StyledArticleLeading> & { withExternalGallery?: boolean },
  ) => ReactNode;
  liveTag: (props?: PartialPropsOf<typeof StyledArticleLiveTag>) => ReactNode;
  moreOnThis: (props?: PartialPropsOf<typeof StyledArticleMoreOnThisList>) => ReactNode;
  paywall: (props?: PartialPropsOf<typeof StyledArticlePaywallContainer>) => ReactNode;
  publishedDate: (props?: PartialPropsOf<typeof StyledArticleDate>) => ReactNode;
  readingTime: (props?: PartialPropsOf<typeof StyledArticleReadingTime>) => ReactNode;
  relatedTopics: (props?: PartialPropsOf<typeof StyledArticleRelatedTopics>) => ReactNode;
  scmpPollWidget: (props?: PartialPropsOf<typeof StyledArticleScmpPollWidget>) => ReactNode;
  sections: (props?: PartialPropsOf<typeof StyledArticleSections>) => ReactNode;
  series: (props?: PartialPropsOf<typeof StyledArticleSeries>) => ReactNode;
  shareWidget: (props?: PartialPropsOf<typeof StyledArticleShareWidget>) => ReactNode;
  speechPlayer: (props: PartialPropsOf<typeof StyledArticleSpeechInlineWidget>) => ReactNode;
  speechPlayerPortal: (props: PartialPropsOf<typeof ArticleSpeechPlayerPortal>) => ReactNode;
  sponsor: (props?: PartialPropsOf<typeof StyledArticleSponsor>) => ReactNode;
  subHeadline: (props?: PartialPropsOf<typeof StyledContentSubHeadline>) => ReactNode;
  takeOverAdSlot: (props?: PartialPropsOf<typeof TakeoverAdSlot>) => ReactNode;
  topic: (props?: PartialPropsOf<typeof StyledArticleTopic>) => ReactNode;
  topicFollowWidget: (props?: PartialPropsOf<typeof StyledArticleTopicFollowWidget>) => ReactNode;
  topics: (props?: PartialPropsOf<typeof StyledArticleTopics>) => ReactNode;
  topicWidget: (props?: PartialPropsOf<typeof StyledArticleTopicWidget>) => ReactNode;
  vocabBox: (props?: PartialPropsOf<typeof StyledArticleVocabBox>) => ReactNode;
};

export type Props = {
  aiSeoOverride?: AiSeoOverride;
  forceRenderInType?: ArticleRenderType;
  reference: articleRenderArticle$key;
};

const Component: FunctionComponent<Props> = ({ aiSeoOverride, forceRenderInType, reference }) => {
  const content = useFragment(
    graphql`
      fragment articleRenderArticle on Content
      @argumentDefinitions(
        customContents: { type: "[CustomContentInput]", defaultValue: [] }
        bodyType: { type: "BodyFormatTypeEnum", defaultValue: PARAGRAPH }
      ) {
        __typename
        entityId
        ... on Article {
          sponsorType
        }
        ...actionWidgetContent
        ...articleAdditionalInfoBoxArticleContent
        ...articleAuthorBlockArticle
        ...articleAuthorDetailsContent
        ...articleBaseAdvertisingInfoArticle
        ...articleBeforeYouGoWidget
        ...articleCommentsTriggerArticle
        ...articleCorrectionsWidgetArticle
        ...articleDateContent
        ...articleFurtherReadingWidgetArticle
        ...articleImageGalleryContent
        ...articleLeadingArticle
        ...articleMoreOnThisListArticle
        ...articlePianoPaywallContent
        ...articlePrintHeadlineMessageArticle
        ...articleReadingTimeContent
        ...articleRelatedTopicsContent
        ...articleSectionsContent
        ...articleSeriesArticle
        ...articleShareWidgetArticle
        ...articleSpeechInlineWidgetArticle
        ...articleSpeechPlayerPortalArticle
        ...articleSponsorContent
        ...articleTopicContent
        ...articleTopicFollowWidgetArticle
        ...articleTopicsContent
        ...articleTopicWidgetArticle
        ...articleVocabBoxArticle
        ...authorAvatarsArticle
        ...authorNamesArticle
        ...authorShortBioArticle
        ...contentActionBarContent
        ...contentBodyContent @arguments(customContents: $customContents, bodyType: $bodyType)
        ...contentHeadlineContent
        ...contentHeadlineTagContent
        ...contentLiveTagContent
        ...contentReadMoreButtonContent
        ...contentSchemaRenderContent
        ...contentSubHeadlineContent
        ...contentSummaryContent
        ...genericArticleContent
        ...globalCheckerContent
        ...helpersCheckArticleIsInSectionsArticle
        ...helpersCheckArticleRenderTypeArticle
        ...helpersCheckIsVideoContent
        ...helpersGetArticleTypeArticle
        ...helpersIsPlusInlinePaywall
        ...infographicArticleArticle
        ...liveArticleArticle
        ...morningStudioArticleContent
        ...newsletterArticleArticle
        ...pianoIntegrationArticle
        ...plusColumnsArticleContent
        ...plusGenericArticleContent
        ...postMagazineArticleContent
        ...seriesArticleArticle
        ...styleArticleContent
        ...videoArticleContent
      }
    `,
    reference,
  );

  usePianoIntegration(content);
  const advertising = useArticleBaseAdvertisingInfo(content);
  const articleType = getArticleType(content);
  const isPlusInlinePaywall = checkArticleIsPlusInlinePaywall(content);
  const { navigatorInfo } = useAtomValue(appAtom);
  const isBot = useMemo(() => navigatorInfo?.isBot ?? false, [navigatorInfo?.isBot]);
  const isLightHouseCrawler = useMemo(
    () => navigatorInfo?.isLightHouseCrawler ?? false,
    [navigatorInfo?.isLightHouseCrawler],
  );
  const router = useRouter();

  const renderType = useMemo(
    () => forceRenderInType ?? checkArticleRenderType(content, router.query),
    [content, forceRenderInType, router.query],
  );

  const { isMostLikelyToSubscribeUser } = useMostLikelyToSubscribe();
  const { articlePianoPaywallStateMap } = useArticlePianoPaywallContext();

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isScmpSubscriber = asyncRosettaState?.result?.isScmpSubscriber ?? false;
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false;
  const isRosettaReady = asyncRosettaState?.status === "success";

  const { experiment: articleAdDensityExperiment } = useActiveExperiment(
    ExperimentTitleNames.ArticleAdDensity,
  );
  const isMobileArticleInlineAdTestPlanEnabled = useMemo(
    () => articleAdDensityExperiment?.variation?.name === "1",
    [articleAdDensityExperiment?.variation?.name],
  );

  const isShowInlinePaywall = useMemo(() => {
    if (checkIsVideoContent(content)) return false; // Video articles don't have a paywall but still need ads enabled
    if (articleSponsorType.has(content.sponsorType ?? "")) return false;

    // Hide the inline paywall for users who already have access to the article,
    // even if Piano (mis)reports an inline paywall event due to cookie/storage
    // restrictions in some browsers (e.g. Safari ITP, in-app webviews).
    if (isRosettaReady) {
      if (renderType === ArticleRenderType.Plus) {
        if (hasScmpPlusAccessRight) return false;
      } else if (isScmpSubscriber) {
        return false;
      }
    }

    if (articlePianoPaywallStateMap?.[content.entityId] === undefined) return;

    // Non-inline Plus articles have a strict hard paywall and do not show inline paywalls,
    // but we need to return undefined initially so Piano can target the element.
    // isPlusInlinePaywall (e.g. chinaFutureTechBriefing) articles go through the normal inline flow.
    if (renderType === ArticleRenderType.Plus && !isPlusInlinePaywall) return false;

    return (
      articlePianoPaywallStateMap[content.entityId] === "requiresInlinePaywall" ||
      articlePianoPaywallStateMap[content.entityId] === "shownInlinePaywall"
    );
  }, [
    content,
    renderType,
    articlePianoPaywallStateMap,
    isPlusInlinePaywall,
    isRosettaReady,
    isScmpSubscriber,
    hasScmpPlusAccessRight,
  ]);

  const isInlinePaywallDomChanged = useMemo(
    () => articlePianoPaywallStateMap?.[content.entityId] === "shownInlinePaywall",
    [content.entityId, articlePianoPaywallStateMap],
  );

  const { getPaywallPosition } = usePaywallPosition(content);
  const isSponsorArticle = articleSponsorType.has(content.sponsorType ?? "");

  const { customPageTriggers, initialized } = useOptimizelyContext();
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), true);

  useEffect(() => {
    if (!customPageTriggers || !initialized || isDesktopUp || !isScmpSubscriber || isSponsorArticle)
      return;
    customPageTriggers(ExperimentAPINames.ArticleAdDensity);
  }, [initialized, isDesktopUp, isScmpSubscriber, isSponsorArticle, customPageTriggers]);

  const isNonSubOnDesktop = !isScmpSubscriber && isDesktopUp;
  const shouldMuteVideo = !isDesktopUp || isSponsorArticle || !isScmpSubscriber;

  const { ComponentWithExtraProps: IframeWithAutoPlayDisabled } = useContentSchemaRenderIframe({
    enableAutoPlay: isRosettaReady && (isSponsorArticle || isNonSubOnDesktop),
    isMuteVideo: shouldMuteVideo,
  });

  const { isReadMoreClicked, setIsReadMoreClicked } = useContentReadMoreContext();
  const { articleElementReference, setEntityId, setIsContentReady } = useArticleRenderContext();

  const provider = useMemo<DefaultComponentsProvider>(
    () => ({
      actionBar: props => <StyledArticleActionBar reference={content} {...props} />,
      actionWidget: props => <StyledActionWidget reference={content} {...props} />,
      adslot: props => (
        <StyledAdSlot
          isDisabled={isShowInlinePaywall === undefined}
          {...advertising}
          {...props}
          targeting={{
            ...advertising?.targeting,
            ...props?.targeting,
          }}
        />
      ),
      articleAdditionalInfoBox: props => (
        <StyledArticleAdditionalInfoBox contentReference={content} {...props} />
      ),
      authorAvatars: props => <StyledArticleAuthorAvatars reference={content} {...props} />,
      authorBlock: props => <StyledArticleAuthorBlock reference={content} {...props} />,
      authorDetails: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <StyledArticleAuthorsDetails reference={content} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      authorNames: props => <StyledArticleAuthorNames reference={content} {...props} />,
      authorShortBio: props => <StyledArticleAuthorShortBio reference={content} {...props} />,
      beforeYouGoWidget: (provider, props) => (
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <StyledArticleBeforeYouGoWidget provider={provider} reference={content} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      body: props => {
        const { primary: primaryInlineAdSlots, secondary: secondInlineAdSlots } = Object.entries(
          props?.inlineAdSlots ?? {},
        ).reduce(
          (mapping, [key, adSlot]) => {
            if (adSlot) {
              const isPrimary = adSlot.isDisabled === undefined;
              const slot = {
                ...adSlot,
                isDisabled: adSlot?.isDisabled === true || isShowInlinePaywall === undefined,
              } as InlineAdSlotProps["slot"];

              if (isPrimary) mapping.primary[key] = slot;
              mapping.secondary[key] = slot;
            }
            return mapping;
          },
          {
            primary: {} as Record<string, InlineAdSlotProps["slot"]>,
            secondary: {} as Record<string, InlineAdSlotProps["slot"]>,
          },
        );

        const getSeparateIndex = (schema: Schema) => {
          const readMoreSeparateAdUnit = isMobileArticleInlineAdTestPlanEnabled
            ? "m_lrec1"
            : "m_banner1";

          const [targetAdSlotTag] = Object.entries(props?.inlineAdSlots ?? {}).find(
            ([_, slot]) => slot?.adUnit === readMoreSeparateAdUnit,
          ) ?? [isMobileArticleInlineAdTestPlanEnabled ? "2_adTest" : "1"];

          const targetAdSlotIndex = schema.findIndex(
            node => node.type === "inline-ad-slot" && node.attribs?.tag === targetAdSlotTag,
          );

          if (targetAdSlotIndex === -1) return ReadMoreElements;
          return isMobileArticleInlineAdTestPlanEnabled
            ? targetAdSlotIndex + 2
            : targetAdSlotIndex + ReadMoreElements + 1;
        };

        const getDefaultPaywallPosition = (schema: Schema) => {
          if (isMobileArticleInlineAdTestPlanEnabled) return ReadMoreElements;

          const [targetAdSlotTag] = Object.entries(props?.inlineAdSlots ?? {}).find(
            ([_, slot]) => slot?.adUnit === "m_banner1",
          ) ?? [isMobileArticleInlineAdTestPlanEnabled ? "3_adTest" : "1"];

          const targetAdSlotIndex = schema.findIndex(
            node => node.type === "inline-ad-slot" && node.attribs?.tag === targetAdSlotTag,
          );

          return targetAdSlotIndex === -1 ? ReadMoreElements - 1 : targetAdSlotIndex;
        };

        const handleRenderContentBody = () => (
          <>
            <PrimaryContentBody
              {...props}
              advertising={advertising}
              extraComponentMap={{
                iframe: IframeWithAutoPlayDisabled,
                ...props?.extraComponentMap,
              }}
              inlineAdSlots={primaryInlineAdSlots}
              preprocessSchema={schema => {
                const templateProcessedSchema = props?.preprocessSchema?.(schema) ?? schema;
                const separateIndex = getSeparateIndex(templateProcessedSchema);

                if (isBot || isLightHouseCrawler) {
                  return templateProcessedSchema.slice(0, separateIndex);
                }
                if (articleType === articleData.types.live.entityId || checkIsVideoContent(content))
                  return templateProcessedSchema;

                const paywallPosition =
                  getPaywallPosition(templateProcessedSchema) ??
                  getDefaultPaywallPosition(templateProcessedSchema);

                return isShowInlinePaywall
                  ? templateProcessedSchema.slice(0, paywallPosition)
                  : templateProcessedSchema.slice(0, separateIndex);
              }}
              reference={content}
            >
              {!isBot && !isLightHouseCrawler && isShowInlinePaywall && (
                <InlinePaywallOverlayer
                  $height={isMostLikelyToSubscribeUser ? "small" : "normal"}
                  $variant={props?.paywallVariant}
                />
              )}
              {/* Prevent show read more before paywall has been executed */}
              {isShowInlinePaywall === false && !checkIsVideoContent(content) && (
                <ContentReadMoreButtonContainer>
                  <ContentReadMoreButton reference={content} />
                </ContentReadMoreButtonContainer>
              )}
            </PrimaryContentBody>
            <SecondlyContentBody
              {...props}
              $isShow={isReadMoreClicked}
              advertising={advertising}
              extraComponentMap={{
                iframe: IframeWithAutoPlayDisabled,
                ...props?.extraComponentMap,
              }}
              inlineAdSlots={secondInlineAdSlots}
              preprocessSchema={schema => {
                if (
                  articleType === articleData.types.live.entityId ||
                  checkIsVideoContent(content) ||
                  (!isBot &&
                    !isLightHouseCrawler &&
                    renderType !== ArticleRenderType.Plus &&
                    isShowInlinePaywall !== false)
                )
                  return [];

                const templateProcessedSchema = props?.preprocessSchema?.(schema) ?? schema;
                const separateIndex = getSeparateIndex(templateProcessedSchema);
                return templateProcessedSchema.slice(separateIndex);
              }}
              reference={content}
            >
              {/* <ArticlePrintHeadlineMessage
                reference={content}
                variant={props?.printHeadlineVariant}
              /> */}
            </SecondlyContentBody>
          </>
        );

        const contentBody = handleRenderContentBody();
        if (props?.isDisabledArticleImageGallery === true) return contentBody;
        return <ArticleImageGallery reference={content}>{contentBody}</ArticleImageGallery>;
      },
      bottomCarouselWidget: (provider, props) => (
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <StyledArticleCarouselWidget
              {...props}
              items={
                props?.items ?? [
                  provider?.beforeYouGoWidget(provider, {
                    children: provider?.scmpPollWidget({
                      adSlotProps: {
                        breakpoint: theme.breakpoints.down("desktop"),
                        zone: advertising.zone,
                      },
                    }),
                  }),
                  provider?.topicWidget({
                    inlineAds: provider.adslot({
                      adUnit: "m_native2d",
                      breakpoint: theme.breakpoints.down("desktop"),
                      isDisabled: props?.shouldDisableAds ?? false,
                      sizes: ["fluid"],
                      zone: advertising.zone,
                    }),
                  }),
                ]
              }
            />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      commentTrigger: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <StyledCommentContainer>
              <StyledArticleCommentsTrigger reference={content} variant="banner" {...props} />
            </StyledCommentContainer>
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      correction: props => <StyledArticleCorrectionsWidget reference={content} {...props} />,
      furtherReading: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <StyledArticleFurtherReadingWidget reference={content} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      headline: props => <StyledContentHeadline componentTag="h1" reference={content} {...props} />,
      headlineTag: props => <StyledContentHeadlineTag reference={content} {...props} />,
      leading: props =>
        props?.withExternalGallery || props?.skipGallery ? (
          <StyledArticleLeading
            responsiveVariants={{
              desktopUp: ImageSize["1200x800"],
              mobileUp: ImageSize["768x768"],
              tabletUp: ImageSize["1020x680"],
            }}
            {...props}
            advertising={{
              ...advertising,
              ...props?.advertising,
            }}
            reference={content}
          />
        ) : (
          <ArticleImageGallery reference={content}>
            <StyledArticleLeading
              responsiveVariants={{
                desktopUp: ImageSize["1200x800"],
                mobileUp: ImageSize["768x768"],
                tabletUp: ImageSize["1020x680"],
              }}
              {...props}
              advertising={{
                ...advertising,
                ...props?.advertising,
              }}
              reference={content}
            />
          </ArticleImageGallery>
        ),
      liveTag: props => <StyledArticleLiveTag reference={content} {...props} />,
      moreOnThis: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <StyledArticleMoreOnThisList reference={content} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      paywall: props => (
        <StyledArticlePaywallContainer isShowInlinePaywall={isShowInlinePaywall} {...props} />
      ),
      publishedDate: props => <StyledArticleDate reference={content} {...props} />,
      readingTime: props => <StyledArticleReadingTime reference={content} {...props} />,
      relatedTopics: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <StyledArticleRelatedTopics reference={content} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      scmpPollWidget: props => <StyledArticleScmpPollWidget {...props} />,
      sections: props => <StyledArticleSections reference={content} {...props} />,
      series: props => <StyledArticleSeries reference={content} {...props} />,
      shareWidget: props => <StyledArticleShareWidget reference={content} {...props} />,
      speechPlayer: props => (
        <StyledArticleSpeechInlineWidget
          onClick={() => {
            setIsReadMoreClicked?.(true);
          }}
          reference={content}
          variant="generic"
          {...props}
        />
      ),
      speechPlayerPortal: props => (
        <ArticleSpeechPlayerPortal
          onClick={() => {
            setIsReadMoreClicked?.(true);
          }}
          reference={content}
          variant="generic-minify"
          {...props}
        />
      ),
      sponsor: props => <StyledArticleSponsor reference={content} {...props} />,
      subHeadline: props =>
        !(isShowInlinePaywall === true && isMostLikelyToSubscribeUser) && (
          <StyledContentSubHeadline reference={content} {...props} />
        ),
      takeOverAdSlot: props => <TakeoverAdSlot adSlotProps={advertising} {...props} />,
      topic: props => <StyledArticleTopic reference={content} {...props} />,
      topicFollowWidget: props => <StyledArticleTopicFollowWidget reference={content} {...props} />,
      topics: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <StyledArticleTopics reference={content} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      topicWidget: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <StyledArticleTopicWidget reference={content} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      vocabBox: props => <StyledArticleVocabBox reference={content} {...props} />,
    }),
    [
      advertising,
      content,
      articleType,
      getPaywallPosition,
      IframeWithAutoPlayDisabled,
      isBot,
      isLightHouseCrawler,
      isMobileArticleInlineAdTestPlanEnabled,
      isMostLikelyToSubscribeUser,
      isReadMoreClicked,
      isShowInlinePaywall,
      renderType,
      setIsReadMoreClicked,
    ],
  );

  const handleRenderApplicationArticle = useCallback(() => {
    switch (renderType) {
      case ArticleRenderType.Generic:
        return (
          <DynamicGenericArticle
            aiSeoOverride={aiSeoOverride}
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={content}
          />
        );
      case ArticleRenderType.Infographic:
      case ArticleRenderType.InfographicLongRead:
        return (
          <DynamicInfographicArticle
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={content}
          />
        );
      case ArticleRenderType.Live:
        return (
          <DynamicLiveArticle
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={content}
          />
        );
      case ArticleRenderType.LongRead:
      case ArticleRenderType.PhotoEssay:
        return (
          <DynamicPhotoEssayAndLongReadArticle
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={content}
          />
        );
      case ArticleRenderType.MorningStudio:
        return <DynamicMorningStudioArticle provider={provider} reference={content} />;
      case ArticleRenderType.Newsletter:
        return <DynamicNewsletterArticle provider={provider} reference={content} />;
      case ArticleRenderType.Plus:
        return (
          <DynamicPlusGenericArticle
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={content}
          />
        );
      case ArticleRenderType.PostMagazine:
        return (
          <DynamicPostMagazineArticle
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={content}
          />
        );
      case ArticleRenderType.Series:
        return <DynamicSeriesArticle provider={provider} reference={content} />;
      case ArticleRenderType.Style:
        return <DynamicStyleArticle provider={provider} reference={content} />;
      case ArticleRenderType.Video:
        return <DynamicVideoArticle provider={provider} reference={content} />;
    }
  }, [
    aiSeoOverride,
    content,
    isInlinePaywallDomChanged,
    isShowInlinePaywall,
    provider,
    renderType,
  ]);

  useEffect(() => {
    if (isShowInlinePaywall !== undefined) {
      setIsContentReady?.(true);
      setEntityId?.(content?.entityId);
    }
    return;
  }, [content.entityId, isShowInlinePaywall, setEntityId, setIsContentReady]);

  return (
    <ArticleRenderContainer $renderType={renderType} ref={articleElementReference}>
      <AuthorAvatarContextProvider
        showBorderColor={
          articleType === articleData.types.column.entityId &&
          renderType !== ArticleRenderType.Generic
        }
      >
        <ThemeProvider theme={defaultTheme}>{handleRenderApplicationArticle()}</ThemeProvider>
        <FloatingButton entityId={content.entityId} />
      </AuthorAvatarContextProvider>
    </ArticleRenderContainer>
  );
};

Component.displayName = "ArticleRender";
export const ArticleRender = withArticleRenderContext(Component);
